import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { isCancel } from 'utils/axios';
import { hospitalHeaders } from '@/utils';
import type { ProgressNotePatientsDownloadResponse } from '@/types/census';
import type {
    CCTPatient,
    CcmViewPreference,
    LatestProgressNote,
    MeetingViewMeta,
    MeetingViewPreferences,
    PatientLlosNoteResponse,
    PatientProfile,
    PatientStickyNoteResponse,
    PatientSummariesResponse,
    ProgressNotePatientsDownloadUnit,
    SaveMeetingViewPreferencesRequest,
    SavePatientProgressNoteRequest,
    UnitOption
} from '@/types/patient';
import { getErrorMessage } from 'utils/error-message';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { sanitizeMeetingViewVisibleColumnsForApi } from '@/utils/meeting-view/meetingViewColumns';

export type SavePatientLlosNoteArg = {
    patientId: string;
    llosNote: string;
    llosCopy: string;
    isLlos: boolean;
    hospitalId?: string;
};

export type SavePatientStickyNoteArg = {
    patientId: string;
    stickyNote: string;
    hospitalId?: string;
};
export interface FetchPatientsParams {
    hospitalId?: string;
    search?: string;
    unit?: string;
    unitRangeId?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    weekendDischargeDetail?: boolean;
    /** Empty string / omitted = all; `weekend` | `general` filter handoff type. */
    handOffType?: '' | 'weekend' | 'general';
}

export type FetchPatientSummariesParams = {
    hospitalId?: string;
    search?: string;
    page?: number;
    limit?: number;
};

export type FetchWeekendDischargePatientsParams = Omit<FetchPatientsParams, 'weekendDischargeDetail'>;

/** `GET /patients/weekend-discharge/csv` — server defaults: sortBy=createdAt, sortOrder=ASC. Prefer `unit` over legacy `unitId`. */
export interface DownloadWeekendDischargeCsvParams {
    hospitalId?: string;
    search?: string;
    unit?: string;
    unitId?: string;
    unitRangeId?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    /** `handoffType=weekend|general` — defaults to weekend when omitted. */
    handoffType?: 'weekend' | 'general';
}

interface FetchPatientsResponse {
    items?: CCTPatient[];
    total?: number;
    page?: number;
    limit?: number;
}

const buildPatientsListQueryParams = (params?: FetchPatientsParams) => ({
    search: params?.search || undefined,
    unit: params?.unit || undefined,
    unitRangeId: params?.unitRangeId || undefined,
    page: params?.page,
    limit: params?.limit,
    sortBy: params?.sortBy || undefined,
    sortOrder: params?.sortOrder || undefined,
    weekendDischargeDetail: params?.weekendDischargeDetail ? true : undefined,
    handoffType: params?.handOffType || undefined
});

const buildWeekendDischargeCsvQueryParams = (params?: DownloadWeekendDischargeCsvParams) => ({
    search: params?.search || undefined,
    unit: params?.unit || undefined,
    unitId: params?.unitId || undefined,
    unitRangeId: params?.unitRangeId || undefined,
    sortBy: params?.sortBy || undefined,
    sortOrder: params?.sortOrder || undefined,
    handoffType: params?.handoffType === 'general' ? 'general' : 'weekend'
});

const parsePatientsListResponse = (data: FetchPatientsResponse | CCTPatient[], params?: FetchPatientsParams) => {
    if (Array.isArray(data)) {
        return {
            items: data,
            total: data.length,
            page: params?.page ?? 1,
            limit: params?.limit ?? data.length
        };
    }

    return {
        items: data?.items ?? [],
        total: data?.total ?? 0,
        page: data?.page ?? params?.page ?? 1,
        limit: data?.limit ?? params?.limit ?? DEFAULT_ROWS_PER_PAGE
    };
};

/** `GET /patients` — optional `X-Hospital-Id`. */
export const fetchPatients = createAsyncThunk<
    {
        items: CCTPatient[];
        total: number;
        page: number;
        limit: number;
        hospitalId?: string;
    },
    FetchPatientsParams | undefined,
    { rejectValue: string }
>('patients/fetchPatients', async (params, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<FetchPatientsResponse | CCTPatient[]>('patients', {
            params: buildPatientsListQueryParams(params),
            ...hospitalHeaders(params?.hospitalId),
            signal
        });

        return parsePatientsListResponse(data, params);
    } catch (error) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch patients'));
    }
});

/** `GET /patients/summaries` — query: search, page, limit; optional `X-Hospital-Id`. */
export const fetchPatientSummaries = createAsyncThunk<
    PatientSummariesResponse,
    FetchPatientSummariesParams | undefined,
    { rejectValue: string }
>('patients/fetchPatientSummaries', async (params, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<PatientSummariesResponse>('patients/summaries', {
            params: {
                search: params?.search || undefined,
                page: params?.page,
                limit: params?.limit
            },
            ...hospitalHeaders(params?.hospitalId),
            signal
        });

        return {
            items: data?.items ?? [],
            total: data?.total ?? 0,
            page: data?.page ?? params?.page ?? 1,
            limit: data?.limit ?? params?.limit ?? DEFAULT_ROWS_PER_PAGE
        };
    } catch (error) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch patient summaries'));
    }
});

/** `GET /patient-handoffs?type=weekend|general` — optional `X-Hospital-Id`. Same response shape as `/patients`. */
export const fetchWeekendDischargePatients = createAsyncThunk<
    {
        items: CCTPatient[];
        total: number;
        page: number;
        limit: number;
        hospitalId?: string;
    },
    FetchWeekendDischargePatientsParams | undefined,
    { rejectValue: string }
>('patients/fetchWeekendDischargePatients', async (params, { rejectWithValue, signal }) => {
    try {
        const { weekendDischargeDetail: _, handoffType, ...listParams } = buildPatientsListQueryParams(params);
        const { data } = await axiosInstance.get<FetchPatientsResponse | CCTPatient[]>('patient-handoffs', {
            params: {
                ...listParams,
                type: handoffType === 'general' ? 'general' : 'weekend'
            },
            ...hospitalHeaders(params?.hospitalId),
            signal
        });

        return parsePatientsListResponse(data, params);
    } catch (error) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch patient handoffs'));
    }
});

/** `GET /units` — optional `X-Hospital-Id`. */
export const fetchUnits = createAsyncThunk<UnitOption[], { hospitalId?: string }, { rejectValue: string }>(
    'patients/fetchUnits',
    async ({ hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get<{ items?: UnitOption[] }>('units', hospitalHeaders(hospitalId));
            const units = data?.items ?? [];
            return units.filter((unit) => unit.isActive);
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch units'));
        }
    }
);

/** `GET /patients/{id}` — optional `X-Hospital-Id`. */
export const fetchPatientById = createAsyncThunk<PatientProfile, { id: string; hospitalId?: string }, { rejectValue: string }>(
    'patients/fetchPatientById',
    async ({ id, hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get<PatientProfile>(`patients/${id}`, hospitalHeaders(hospitalId));
            return data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch patient'));
        }
    }
);

/** `GET /patients/weekend-discharge/csv` — returns CSV text (`text/csv;charset=utf-8`); optional `X-Hospital-Id`. */
export async function downloadWeekendDischargePatientsCsv(
    params?: DownloadWeekendDischargeCsvParams,
    signal?: AbortSignal
): Promise<string> {
    const { data } = await axiosInstance.get<string>('patients/weekend-discharge/csv', {
        params: buildWeekendDischargeCsvQueryParams(params),
        headers: {
            Accept: 'text/csv;charset=utf-8',
            ...(params?.hospitalId ? { 'X-Hospital-Id': params.hospitalId } : {})
        },
        responseType: 'text',
        signal
    });
    return data;
}
/** `POST /patients/{id}/llos-note` — optional `X-Hospital-Id`. */
export const savePatientLlosNote = createAsyncThunk<PatientLlosNoteResponse, SavePatientLlosNoteArg, { rejectValue: string }>(
    'patients/saveLlosNote',
    async ({ patientId, llosNote, llosCopy, isLlos, hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<PatientLlosNoteResponse>(
                `patients/${encodeURIComponent(patientId)}/llos-note`,
                { llosNote, llosCopy, isLlos },
                hospitalHeaders(hospitalId)
            );
            return {
                ...data,
                llosCopy: data.llosCopy ?? llosCopy,
                isLlos: data.isLlos ?? isLlos
            };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to save LLOS note'));
        }
    }
);

/** `POST /patients/{id}/sticky-note` — optional `X-Hospital-Id`. */
export const savePatientStickyNote = createAsyncThunk<PatientStickyNoteResponse, SavePatientStickyNoteArg, { rejectValue: string }>(
    'patients/saveStickyNote',
    async ({ patientId, stickyNote, hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<PatientStickyNoteResponse>(
                `patients/${encodeURIComponent(patientId)}/sticky-note`,
                { stickyNote },
                hospitalHeaders(hospitalId)
            );
            return data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to save sticky note'));
        }
    }
);

export type FetchProgressNotesPatientsParams = {
    hospitalId?: string;
    search?: string;
    unitId?: string;
    unitRangeId?: string;
    tier?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    /** When omitted, listing defaults to `true`. Pass `null` to omit the param (e.g. patient selection). */
    isSelected?: boolean | null;
    /** Empty string / omitted = all; `weekend` | `general`. */
    handOffType?: '' | 'weekend' | 'general';
};

/** Builds query params for `GET /progress-note-patients`. */
const buildProgressNotesListQueryParams = (params?: FetchProgressNotesPatientsParams) => {
    const resolvedIsSelected = params?.isSelected === undefined ? true : params.isSelected;

    return {
        search: params?.search || undefined,
        unitId: params?.unitId || undefined,
        unitRangeId: params?.unitRangeId || undefined,
        tier: params?.tier || undefined,
        page: params?.page,
        limit: params?.limit,
        sortBy: params?.sortBy || undefined,
        sortOrder: params?.sortOrder || undefined,
        isSelected: resolvedIsSelected === null ? undefined : resolvedIsSelected,
        handoffType: params?.handOffType || undefined
    };
};

/** `GET /progress-note-patients` — optional `X-Hospital-Id`. Query: sortBy, sortOrder, isSelected, search, unitId, unitRangeId, tier, type, page, limit. */
const getProgressNotesPatients = async (params: FetchProgressNotesPatientsParams | undefined, signal?: AbortSignal) => {
    const { data } = await axiosInstance.get<FetchPatientsResponse | CCTPatient[]>('progress-note-patients', {
        params: buildProgressNotesListQueryParams(params),
        ...hospitalHeaders(params?.hospitalId),
        signal
    });

    return parsePatientsListResponse(data, params);
};

/** `GET /progress-note-patients` with `isSelected=true` — assigned patients for the progress notes listing. */
export const fetchProgressNotesPatients = createAsyncThunk<
    {
        items: CCTPatient[];
        total: number;
        page: number;
        limit: number;
        hospitalId?: string;
    },
    FetchProgressNotesPatientsParams | undefined,
    { rejectValue: string }
>('patients/fetchProgressNotesPatients', async (params, { rejectWithValue, signal }) => {
    try {
        return await getProgressNotesPatients({ ...params, isSelected: params?.isSelected ?? true }, signal);
    } catch (error) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch progress notes patients'));
    }
});

/** `GET /progress-note-patients` without `isSelected` — all patients for patient selection. */
export const fetchProgressNotesSelectionPatients = createAsyncThunk<
    {
        items: CCTPatient[];
        total: number;
        page: number;
        limit: number;
        hospitalId?: string;
    },
    FetchProgressNotesPatientsParams | undefined,
    { rejectValue: string }
>('patients/fetchProgressNotesSelectionPatients', async (params, { rejectWithValue, signal }) => {
    try {
        return await getProgressNotesPatients({ ...params, isSelected: null }, signal);
    } catch (error) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch progress notes selection patients'));
    }
});

export type BulkProgressNotePatientsParams = {
    patientIds: string[];
    hospitalId?: string;
};

/** `POST /progress-note-patients/bulk-assign` */
export const bulkAssignProgressNotePatients = createAsyncThunk<unknown, BulkProgressNotePatientsParams, { rejectValue: string }>(
    'patients/bulkAssignProgressNotePatients',
    async ({ patientIds, hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<unknown>(
                'progress-note-patients/bulk-assign',
                { patientIds },
                hospitalHeaders(hospitalId)
            );
            return data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to bulk assign progress note patients'));
        }
    }
);

/** `POST /progress-note-patients/bulk-unassign` */
export const bulkUnassignProgressNotePatients = createAsyncThunk<unknown, BulkProgressNotePatientsParams, { rejectValue: string }>(
    'patients/bulkUnassignProgressNotePatients',
    async ({ patientIds, hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<unknown>(
                'progress-note-patients/bulk-unassign',
                { patientIds },
                hospitalHeaders(hospitalId)
            );
            return data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to bulk unassign progress note patients'));
        }
    }
);

export type DownloadProgressNotePatientsParams = {
    hospitalId?: string;
    /**
     * Unit filter: omit or `assigned-units` for the caller’s assigned unit room ranges;
     * `hospital-wide` for unrestricted users; `all` for every unit; a unit code (e.g. `2N`);
     * a unit UUID; or comma-separated unit UUIDs.
     */
    unit?: ProgressNotePatientsDownloadUnit;
    /** Filter by unit room range primary key (uuid). */
    unitRangeId?: string;
};

/** `GET /progress-note-patients/download` — optional `X-Hospital-Id`. Query: unit, unitRangeId. */
export const downloadProgressNotePatients = createAsyncThunk<
    ProgressNotePatientsDownloadResponse,
    DownloadProgressNotePatientsParams | undefined,
    { rejectValue: string }
>('patients/downloadProgressNotePatients', async (params, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<ProgressNotePatientsDownloadResponse>('progress-note-patients/download', {
            params: {
                unit: params?.unit || undefined,
                unitRangeId: params?.unitRangeId || undefined
            },
            ...hospitalHeaders(params?.hospitalId),
            signal
        });
        return data;
    } catch (error) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to download progress note patients'));
    }
});

export type SavePatientProgressNoteArg = {
    body: SavePatientProgressNoteRequest;
    hospitalId?: string;
};

export type FetchMeetingViewPatientsParams = {
    hospitalId?: string;
    search?: string;
    unit?: string;
    unitId?: string;
    unitRangeId?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
};

type FetchMeetingViewPatientsResponse = FetchPatientsResponse & {
    meta?: MeetingViewMeta;
};

const buildMeetingViewListQueryParams = (params?: FetchMeetingViewPatientsParams) => ({
    search: params?.search || undefined,
    unit: params?.unit || undefined,
    unitId: params?.unitId || undefined,
    unitRangeId: params?.unitRangeId || undefined,
    page: params?.page,
    limit: params?.limit,
    sortBy: params?.sortBy || undefined,
    sortOrder: params?.sortOrder || undefined
});

/** `GET /patients/meeting-view` — optional `X-Hospital-Id`. */
export const fetchMeetingViewPatients = createAsyncThunk<
    {
        items: CCTPatient[];
        total: number;
        page: number;
        limit: number;
        meta: MeetingViewMeta | null;
        hospitalId?: string;
    },
    FetchMeetingViewPatientsParams | undefined,
    { rejectValue: string }
>('patients/fetchMeetingViewPatients', async (params, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<FetchMeetingViewPatientsResponse | CCTPatient[]>('patients/meeting-view', {
            params: buildMeetingViewListQueryParams(params),
            ...hospitalHeaders(params?.hospitalId),
            signal
        });

        const parsed = parsePatientsListResponse(data, params);
        const meta = Array.isArray(data) ? null : (data?.meta ?? null);

        return { ...parsed, meta };
    } catch (error) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch meeting view patients'));
    }
});

/** `GET /patients/meeting-view/preferences` — optional `X-Hospital-Id`. */
export const fetchMeetingViewPreferences = createAsyncThunk<MeetingViewPreferences, { hospitalId?: string }, { rejectValue: string }>(
    'patients/fetchMeetingViewPreferences',
    async ({ hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get<MeetingViewPreferences>(
                'patients/meeting-view/preferences',
                hospitalHeaders(hospitalId)
            );
            return data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch meeting view preferences'));
        }
    }
);

export type SaveMeetingViewPreferencesArg = {
    body: SaveMeetingViewPreferencesRequest;
    hospitalId?: string;
};

/** `PUT /patients/meeting-view/preferences` — optional `X-Hospital-Id`. */
export const saveMeetingViewPreferences = createAsyncThunk<MeetingViewPreferences, SaveMeetingViewPreferencesArg, { rejectValue: string }>(
    'patients/saveMeetingViewPreferences',
    async ({ body, hospitalId }, { rejectWithValue }) => {
        try {
            const sanitizedBody: SaveMeetingViewPreferencesRequest = {
                ...body,
                visibleColumns: sanitizeMeetingViewVisibleColumnsForApi(body.visibleColumns)
            };
            const { data } = await axiosInstance.put<MeetingViewPreferences>(
                'patients/meeting-view/preferences',
                sanitizedBody,
                hospitalHeaders(hospitalId)
            );
            return data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to save meeting view preferences'));
        }
    }
);

export type CcmViewPreferenceArg = {
    hospitalId: string;
};

/** `GET /patient-progress-notes/ccm-view-preference` — header: `X-Hospital-Id`. */
export const fetchCcmViewPreference = createAsyncThunk<CcmViewPreference, CcmViewPreferenceArg, { rejectValue: string }>(
    'patients/fetchCcmViewPreference',
    async ({ hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get<CcmViewPreference>(
                'patient-progress-notes/ccm-view-preference',
                hospitalHeaders(hospitalId)
            );
            return data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch CCM view preference'));
        }
    }
);

export type SaveCcmViewPreferenceArg = {
    hospitalId: string;
    view: CcmViewPreference['view'];
    visibleColumns?: string[];
    columnOrder?: string[];
};

/** `PUT /patient-progress-notes/ccm-view-preference` — header: `X-Hospital-Id`. `view` is required; column lists are sent only when changed. */
export const saveCcmViewPreference = createAsyncThunk<CcmViewPreference, SaveCcmViewPreferenceArg, { rejectValue: string }>(
    'patients/saveCcmViewPreference',
    async ({ hospitalId, view, visibleColumns, columnOrder }, { rejectWithValue }) => {
        try {
            const body: { view: CcmViewPreference['view']; visibleColumns?: string[]; columnOrder?: string[] } = { view };
            if (visibleColumns !== undefined) {
                body.visibleColumns = visibleColumns;
            }
            if (columnOrder !== undefined) {
                body.columnOrder = columnOrder;
            }

            const { data } = await axiosInstance.put<CcmViewPreference>(
                'patient-progress-notes/ccm-view-preference',
                body,
                hospitalHeaders(hospitalId)
            );
            return data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to save CCM view preference'));
        }
    }
);

/** `POST /patient-progress-notes` — create or update; response uses ISO `silverstayAddDt` (`YYYY-MM-DD`). */
export const savePatientProgressNote = createAsyncThunk<LatestProgressNote, SavePatientProgressNoteArg, { rejectValue: string }>(
    'patients/savePatientProgressNote',
    async ({ body, hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<LatestProgressNote>('patient-progress-notes', body, hospitalHeaders(hospitalId));
            return data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to save progress note'));
        }
    }
);
